export interface LearningSection {
  uid: string;
  title: string;
  subtitle: string;
  imageSrc: string;
  order: number;
  status: 'draft' | 'published';
  isSupportOnly: boolean;
  lessons: LearningLesson[];
}

export interface LearningLesson {
  uid: string;
  title: string;
  imageSrc: string;
  description: string;
  isFree: boolean;
  timeMinutes: number;
  type: 'lesson' | 'quiz';
  status: 'draft' | 'published';
  order?: number;
  color?: string;
}

export interface LearningLessonPage {
  uid: string;
  originalUid?: string; // Track the database ID separately from the editable ID
  content: string;
  order: number;
  buttonText: string;
  type: 'page';
}

export interface QuestionOption {
  uid: string;
  text: string;
  aiFriendlyText?: string;
}

export interface LearningLessonQuestionPage extends Omit<LearningLessonPage, 'type'> {
  type: 'question';
  shouldSaveAnswer: boolean;
  aiFriendlyQuestion?: string;
  options: QuestionOption[];
  correctOptionIds: string[];
  correctAnswerReaction?: string;
  incorrectAnswerReaction?: string;
}

// Union type for all page types
export type LessonPage = LearningLessonPage | LearningLessonQuestionPage;

export interface LearningLessonWithPages extends LearningLesson {
  pages: LessonPage[];
}

// Form interfaces for creating/editing
export interface CreateSectionData {
  id?: string;
  title: string;
  subtitle: string;
  imageSrc: string;
  status: 'draft' | 'published';
  isSupportOnly: boolean;
  order: number;
}

export interface CreateLessonData {
  id?: string;
  title: string;
  imageSrc: string;
  description: string;
  isFree: boolean;
  timeMinutes: number;
  type: 'lesson' | 'quiz';
  status: 'draft' | 'published';
  order: number;
  color?: string;
}

interface BasePageData {
  uid?: string;
  content: string;
  order: number;
  buttonText: string;
}

export interface CreatePageData extends BasePageData {
  type: 'page';
}

export interface CreateQuestionPageData extends BasePageData {
  type: 'question';
  shouldSaveAnswer: boolean;
  aiFriendlyQuestion?: string;
  options: QuestionOption[];
  correctOptionIds: string[];
  correctAnswerReaction?: string;
  incorrectAnswerReaction?: string;
}

// Union type for create page data
export type CreateAnyPageData = CreatePageData | CreateQuestionPageData;

// Update types
export type UpdateSectionData = Partial<CreateSectionData>;
export type UpdateLessonData = Partial<CreateLessonData>;
export type UpdatePageData = Partial<CreatePageData>;
export type UpdateQuestionPageData = Partial<CreateQuestionPageData>;
export type UpdateAnyPageData = UpdatePageData | UpdateQuestionPageData;

// Type guards
export function isQuestionPage(page: LessonPage): page is LearningLessonQuestionPage {
  return page.type === 'question';
}

export function isRegularPage(page: LessonPage): page is LearningLessonPage {
  return page.type === 'page';
}

// Export/Import types
export interface ExportedSection {
  section: Omit<LearningSection, 'lessons'>;
  lessons: ExportedLesson[];
}

export interface ExportedLesson {
  lesson: LearningLesson;
  pages: LessonPage[];
}

export interface ExportData {
  version: string;
  exportDate: string;
  type: 'section' | 'lessons';
  data: ExportedSection | ExportedLesson[];
}

export interface ImportOptions {
  overwriteExisting: boolean;
  preserveIds: boolean;
  targetSectionId?: string;
}

export interface ImportResult {
  success: boolean;
  message: string;
  importedIds: {
    sectionIds: string[];
    lessonIds: string[];
    pageIds: string[];
  };
  errors: string[];
}

export interface AdminGetLessonRequest {
  sectionId: string;
  lessonId: string;
  userId: string;
}

export interface LessonPreviewPageResponse {
  uid: string;
  content: string;
  order: number;
  buttonText: string;
  type: 'page' | 'question';
  shouldSaveAnswer?: boolean;
  aiFriendlyQuestion?: string;
  options?: QuestionOption[];
  correctOptionIds?: string[];
  correctAnswerReaction?: string;
  incorrectAnswerReaction?: string;
  hasCustomizedContent?: boolean;
  customizedFallbacks?: Record<string, string>;
}

export interface LessonPreviewResponse {
  uid: string;
  title: string;
  imageSrc: string;
  description: string;
  isFree: boolean;
  timeMinutes: number;
  type: 'lesson' | 'quiz';
  status: 'draft' | 'published';
  order?: number;
  color?: string;
  pages: LessonPreviewPageResponse[];
}
